import React, {useEffect, useState} from "react";
import {styled} from "@mui/system";
import {CheckCircleOutline, Done, MoreVert, WarningAmber} from "@mui/icons-material";
import {DataGrid} from "@mui/x-data-grid";
import Chip from "@mui/material/Chip";
import {useNavigate} from "react-router-dom";
import {TextField, Typography} from "@material-ui/core";
import {ApiDetails} from "../../../../dummyData";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import LoadingButton from "@mui/lab/LoadingButton";
import SendIcon from "@mui/icons-material/Send";
import Slide from "@mui/material/Slide";
import {useConfirm} from "material-ui-confirm";
import {showSnackbar} from "../../../../components/SnackBar/SnackBar";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import WaterDropIcon from "@mui/icons-material/Water";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import DownloadIcon from "@mui/icons-material/Download";
import InfoIcon from "@mui/icons-material/Info";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import CircularProgress from "@mui/material/CircularProgress";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import {alpha, useTheme} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CloseIcon from "@mui/icons-material/Close";

// Enhanced DataGrid styling - fixed header overlap issue
const StyledDataGrid = styled(DataGrid)(({theme}) => ({
    "& .MuiDataGrid-root": {
        height: "100%",
    },
    "& .MuiDataGrid-columnHeaders": {
        fontSize: "1rem",
        fontWeight: 700,
        backgroundColor: "#2C5283",
        color: "#FFFFFF",
        // paddingTop: "10px",
        // paddingBottom: "10px",
        // Fix header overlap
        zIndex: 5,
    },
    "& .MuiDataGrid-row:nth-of-type(even)": {
        backgroundColor: "#f7f9fc",
    },
    "& .MuiDataGrid-row:hover": {
        backgroundColor: "#e8f0fe",
    },
    "& .MuiDataGrid-cell:focus": {
        outline: "none",
    },
    "& .MuiDataGrid-cell": {
        fontSize: "0.9rem",
        padding: "12px 16px",
        borderBottom: "1px solid #eaeaea",
    },
    "& .MuiDataGrid-footerContainer": {
        borderTop: "1px solid #eaeaea",
        backgroundColor: "#f7f9fc",
    },
    // Fix header row height to prevent overlap with first data row
    "& .MuiDataGrid-columnHeaderRow": {
        minHeight: "56px !important",
    },
    // Add padding to first row to prevent overlap with header
    "& .MuiDataGrid-row:first-of-type": {
        paddingTop: "4px",
    },
    "& .MuiDataGrid-virtualScroller": {
        marginTop: "56px !important", // Add margin to prevent content overlap with header
    }
}));

const SearchBox = styled(TextField)({
    "& .MuiOutlinedInput-root": {
        borderRadius: "8px",
        backgroundColor: "#fff",
        "& fieldset": {
            borderColor: "#e0e0e0",
        },
        "&:hover fieldset": {
            borderColor: "#2C5283",
        },
        "&.Mui-focused fieldset": {
            borderColor: "#2C5283",
        },
    },
    "& .MuiInputLabel-root": {
        fontSize: "0.95rem",
    }
});


// Slide transition for smoother opening
const Transition = React.forwardRef((props, ref) => (
    <Slide direction="up" ref={ref} {...props} />
));

// Enhanced styled components with more modern elements
const styles = {
    dialogTitle: {
        backgroundColor: "#2C5283",
        color: "white",
        padding: "16px 24px",
        fontWeight: "bold",
        display: "flex",
        alignItems: "center",
        gap: "10px",
    },
    dialogContent: {
        padding: "24px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        // Ensure contents fit without requiring scrolling
        overflow: "visible",
    },
    container: {
        width: "100%",
        maxWidth: "90vw",
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
        overflow: "visible", // Allow content to expand fully
    },
    billingHeader: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        marginBottom: "20px",
    },
    label: {
        fontSize: "1rem",
        fontWeight: 600,
        color: "#444",
    },
    cancelButton: {
        color: "#d32f2f",
        borderColor: "#d32f2f",
        borderRadius: "8px",
        padding: "10px 24px",
        textTransform: "none",
        fontWeight: 600,
        "&:hover": {
            backgroundColor: "rgba(211, 47, 47, 0.1)",
        },
    },
    billButton: {
        backgroundColor: "#2C5283",
        color: "white",
        borderRadius: "8px",
        padding: "10px 24px",
        textTransform: "none",
        fontWeight: 600,
        "&:hover": {
            backgroundColor: "#1E3A63",
        },
    },
    downloadButton: {
        backgroundColor: "#388e3c",
        color: "white",
        borderRadius: "8px",
        padding: "10px 20px",
        textTransform: "none",
        fontWeight: 600,
        "&:hover": {
            backgroundColor: "#2e7d32",
        },
        display: "flex",
        alignItems: "center",
        gap: "8px",
    },
    monthSelection: {
        padding: "20px",
        backgroundColor: "#f7f9fc",
        borderRadius: "12px",
        marginBottom: "24px",
        border: "1px solid #e0e7ff",
    },
    infoBox: {
        padding: "16px",
        backgroundColor: "#e3f2fd",
        borderRadius: "8px",
        marginTop: "8px",
        marginBottom: "16px",
        border: "1px solid #bbdefb",
    },
    statusChip: {
        fontWeight: 600,
        borderRadius: "16px",
    },
    tableContainer: {
        marginTop: "16px",
        padding: "16px",
        backgroundColor: "#f7f9fc",
        borderRadius: "8px",
    },
    // Add a wrapper style for the DataGrid to control height
    gridWrapper: {
        height: "auto",
        width: "100%",
        marginBottom: "20px",
    },
    searchHeader: {
        padding: "12px 16px",
        backgroundColor: "#f5f7fa",
        borderRadius: "8px",
        border: "1px solid #e0e0e0",
        marginBottom: "16px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
    },
};

const PropertyTenants = (props) => {
    const navigate = useNavigate();
    const theme = useTheme();

    const {
        originalText,
        waterBill,
        minimumWaterBill,
        electricityBill,
        minimumElectricityBill,
        propertyDetails,
        viewModal
    } = props;

    const confirm = useConfirm();

    // State definitions
    let [billedTenants, setBilledTenants] = useState([]);
    let [elecBilledTenants, setElecBilledTenants] = useState([]);
    const [removedWaterBilling, setRemovedWaterBilling] = useState([]);
    const [removedElectricityBilling, setRemovedElectricityBilling] = useState([]);
    const [month, setMonth] = useState(getBillingMonth());
    const [selectedMonth, setSelectedMonth] = useState(getBillingMonth());
    const [tenants, setTenants] = useState([]);
    const [update, RunUpdateTenants] = useState(false);
    const [tenantsLength, setTenantsLength] = useState(0);
    const [htmlContent, setHtmlContent] = useState('');
    const [billLoading, setBillLoading] = useState(false);

    // Search state
    const [fetched, setFetched] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredTenants, setFilteredTenants] = useState([]);

    const label = {inputProps: {"aria-label": "Switch demo"}};
    const billTenantsModal = viewModal;

    function getMonthName(monthNumber) {
        const date = new Date();
        date.setMonth(monthNumber);

        return date.toLocaleString("en-US", {month: "long"});
    }

    function getBillingMonth() {
        const today = new Date();
        const year = today.getFullYear();
        const month = today.getMonth(); // 0-based index (Jan = 0, Feb = 1, etc.)
        const day = today.getDate();

        if (day >= new Date(year, month + 1, 0).getDate() - 4 || day <= 10) {
            // Last 5 days of the month OR first 10 days of the new month
            return new Date(year, month + 1, 1); // Select Next Month
        } else {
            return new Date(year, month, 1); // Select Current Month
        }
    }

    // Search functionality
    useEffect(() => {
        if (searchTerm.trim() === "") {
            setFilteredTenants(tenants);
            return;
        }

        const lowercasedSearch = searchTerm.toLowerCase();
        const filtered = tenants.filter(tenant =>
            // Search by name
            (tenant.first_name?.toLowerCase().includes(lowercasedSearch) ||
                tenant.last_name?.toLowerCase().includes(lowercasedSearch) ||
                // Search by house number
                tenant.house_number?.toLowerCase().includes(lowercasedSearch) ||
                // Search by phone number
                tenant.phone_number?.toLowerCase().includes(lowercasedSearch))
        );

        setFilteredTenants(filtered);
    }, [searchTerm, tenants]);

// Handle search input change
    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

// Clear search
    const handleClearSearch = () => {
        setSearchTerm("");
    };

    useEffect(() => {
        fetch(ApiDetails + "pegasus/visionary/tenant/get/property/tenants", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
            },
            body: JSON.stringify({property_id: propertyDetails.property_id}),
        })
            .then(async (response) => {
                let data = await response.json();
                if (response.status === 200) {
                    setTenantsLength(data.length);
                    setTenants(data);
                    setFetched(true)
                }
            })
            .catch((err) => console.log(err));
    }, [update, propertyDetails]);

    // Get row styling based on billing enabled status
    const getRowStyle = (params) => ({
        backgroundColor: params.row.billing_enabled ? "transparent" : "#f0f0f0",
        color: params.row.billing_enabled ? "inherit" : "#888",
        pointerEvents: params.row.billing_enabled ? "auto" : "none",
        opacity: params.row.billing_enabled ? 1 : 0.6,
    });

    // Enhanced columns with icons and better styling
    const columns = [
        {
            field: "user",
            headerAlign: "center",
            headerName: "Name",
            minWidth: 200,
            flex: 1,
            renderCell: (params) => (
                <div style={{display: "flex", alignItems: "center"}}>
                    {params.row.state === "NOTICE" ? (
                        <WarningAmber style={{color: "#F57C00", marginRight: 8}}/>
                    ) : (
                        <CheckCircleOutline style={{color: "#4CAF50", marginRight: 8}}/>
                    )}
                    <span style={{fontWeight: 500}}>{params.row.first_name} {params.row.last_name}</span>
                </div>
            ),
        },
        {
            field: "house_number",
            headerAlign: "center",
            align: "center",
            headerName: "HSE NO",
            minWidth: 100,
            flex: 1,
        },
        {
            field: "phone_number",
            headerAlign: "center",
            align: "center",
            headerName: "Phone Number",
            minWidth: 150,
            flex: 1,
        },
        {
            field: "rent",
            headerName: "Rent",
            minWidth: 120,
            flex: 1,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <span style={{fontWeight: 600, color: "#2E7D32"}}>
      {parseInt(params.row.rent.toString().replace(",", "")).toLocaleString("en-US", {
          style: "currency",
          currency: "KSH",
      })}
    </span>
            ),
        },
        {
            field: "bill",
            headerName: "Wallet",
            minWidth: 120,
            flex: 1,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (
                <span style={{fontWeight: 600, color: params.row.status === "PENDING" ? "#D32F2F" : "#388E3C"}}>
      {params.row.Wallet.toLocaleString("en-US", {
          style: "currency",
          currency: "KSH",
      })}
    </span>
            ),
        },
        {
            field: "status",
            headerName: "Status",
            minWidth: 120,
            flex: 1,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => {
                switch (params.row.status) {
                    case "PENDING":
                        return <Chip label="PENDING" color="error" variant="outlined" sx={styles.statusChip}/>;
                    case "COMPLETED":
                        return <Chip label="PAID" color="success" variant="outlined" icon={<Done/>}
                                     sx={styles.statusChip}/>;
                    default:
                        return <Chip label="PARTIAL" color="warning" variant="outlined" sx={styles.statusChip}/>;
                }
            },
        },
        {
            field: "login_successful",
            headerName: "Logged in",
            flex: 1,
            minWidth: 80,
            headerAlign: "center",
            renderCell: (params) => (
                params.row.login_successful ? (
                    <Chip label="Yes" color="success" variant="outlined" icon={<Done/>} sx={styles.statusChip}/>
                ) : (
                    <Chip label="No" color="error" variant="outlined" icon={<WarningAmber/>} sx={styles.statusChip}/>
                )
            ),
        },
        {
            field: "action",
            headerName: "Actions",
            minWidth: 230,
            flex: 1,
            headerAlign: "center",
            renderCell: (params) => (
                <Chip
                    label="View Details"
                    color="info"
                    onClick={() => navigate(`/tenant/${params.row.tenant_id}`, {
                        state: {tenant_id: params.row.tenant_id},
                    })}
                    onDelete={() => navigate(`/tenant/${params.row.tenant_id}`, {
                        state: {tenant_id: params.row.tenant_id},
                    })}
                    deleteIcon={<MoreVert/>}
                    variant="outlined"
                    style={{cursor: "pointer"}}
                    sx={styles.statusChip}
                />
            ),
        },
    ];

    // Enhanced billing columns with icons and better styling
    const billingColumns = [
        {
            field: "user",
            headerName: "Name",
            flex: 1,
            minWidth: 120,
            sortOrder: "asc",
            renderCell: (params) => (
                <div className="userListUser" style={getRowStyle(params)}>
                    {params.row.first_name} {params.row.last_name}
                </div>
            ),
        },
        {
            field: "house_number",
            headerName: "HSE NO",
            flex: 1,
            minWidth: 80,
            renderCell: (params) => (
                <div style={getRowStyle(params)}>{params.row.house_number}</div>
            ),
        },
        {
            field: "rent",
            headerName: "Rent",
            flex: 1,
            minWidth: 50,
            renderCell: (params) => (
                <div style={{...getRowStyle(params), color: "green", fontWeight: 600}}>
                    {parseInt(params.row.rent.toString().replace(",", "")).toLocaleString("en-US", {
                        style: "currency",
                        currency: "KSH",
                    })}
                </div>
            ),
        },
        {
            field: "bill",
            headerName: "Wallet",
            minWidth: 120,
            flex: 1,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (
                <span
                    style={{
                        ...getRowStyle(params),
                        color: params.row.status === "PENDING" ? "#D32F2F" : "#388E3C",
                        fontWeight: 600
                    }}
                >
            {params.row.Wallet.toLocaleString("en-US", {
                style: "currency",
                currency: "KSH",
            })}
        </span>
            ),
        },

        {
            field: "meter_reading",
            headerName: "Water",
            flex: 1,
            minWidth: 120,
            renderCell: (params) => {
                return (
                    <Box sx={{display: "flex", alignItems: "center", gap: 1, ...getRowStyle(params)}}>
                        <WaterDropIcon fontSize="small" sx={{color: "#2196F3"}}/>
                        <span>{params.row.meter_reading}</span>
                    </Box>
                );
            },
        },
        {
            field: "current_meter_reading",
            headerName: "Water Reading",
            flex: 1,
            minWidth: 100,
            renderCell: (params) => {
                return (<div className="userListAction">
                    <TextField
                        type={"number"}
                        placeholder="Enter Reading ..."
                        onChange={(e) => {
                            let filteredTenantArray = billedTenants.filter((tenant) => {
                                return tenant.tenant_id !== params.row.tenant_id;
                            });

                            if (e.target.value === undefined || e.target.value === null || e.target.value === "") {
                                return;
                            }

                            let consumedUnits = e.target.value - params.row.meter_reading;
                            consumedUnits = consumedUnits.toFixed(1);

                            let finalWaterBill = parseFloat(consumedUnits) * parseInt(waterBill);

                            if (finalWaterBill < minimumWaterBill) {
                                finalWaterBill = minimumWaterBill;
                            }

                            setBilledTenants([...filteredTenantArray, {
                                tenant_id: params.row.tenant_id,
                                rent: parseInt(params.row.rent.replace(",", "")),
                                arrears: parseInt(params.row.arrears),
                                finalWaterBill: parseFloat(finalWaterBill),
                                total_bill: parseInt(params.row.rent.replace(",", "")) + parseInt(params.row.arrears) + parseFloat(finalWaterBill),
                                meter_reading: e.target.value,
                                landlord_id: JSON.parse(originalText)[0][1],
                            },]);
                        }}
                        disabled={!params.row.billing_enabled}
                        InputProps={{
                            startAdornment: <WaterDropIcon style={{color: "#2196F3", marginRight: "8px"}}/>,
                        }}
                        variant="outlined"
                        size="small"
                        style={{
                            backgroundColor: "white",
                            ...getRowStyle(params)
                        }}
                    />
                </div>);
            },
        },

        {
            field: "electricity_meter_reading",
            headerName: "Elec Reading",
            flex: 1,
            minWidth: 80,
            renderCell: (params) => {
                return (
                    <Box sx={{display: "flex", alignItems: "center", gap: 1, ...getRowStyle(params)}}>
                        <ElectricBoltIcon fontSize="small" sx={{color: "#FF9800"}}/>
                        <span>{params.row.electricity_meter_reading}</span>
                    </Box>
                );
            },
        },
        {
            field: "current_electricity_meter_reading",
            headerName: "Electricity Reading",
            flex: 1,
            minWidth: 120,
            renderCell: (params) => {
                return (<div className="userListAction">
                    <TextField
                        type={"number"}
                        placeholder="Enter Reading ..."
                        onChange={(e) => {
                            let filteredTenantArray = elecBilledTenants.filter((tenant) => {
                                return tenant.tenant_id !== params.row.tenant_id;
                            });
                            if (e.target.value === undefined || e.target.value === null || e.target.value === "") {
                                return;
                            }
                            let consumedUnits = e.target.value - params.row.electricity_meter_reading;
                            consumedUnits = consumedUnits.toFixed(1);

                            let finalElecBill = parseFloat(consumedUnits) * parseInt(electricityBill);

                            if (finalElecBill < minimumElectricityBill) {
                                finalElecBill = minimumElectricityBill;
                            }

                            setElecBilledTenants([...filteredTenantArray, {
                                tenant_id: params.row.tenant_id,
                                rent: parseInt(params.row.rent.replace(",", "")),
                                arrears: parseInt(params.row.arrears),
                                electricity_bill: parseFloat(finalElecBill),
                                total_bill: parseInt(params.row.rent.replace(",", "")) + parseInt(params.row.arrears) + parseFloat(finalElecBill),
                                electricity_meter_reading: e.target.value,
                                landlord_id: JSON.parse(originalText)[0][1],
                            },]);
                        }}
                        disabled={!params.row.billing_enabled}
                        InputProps={{
                            startAdornment: <ElectricBoltIcon style={{color: "#FF9800", marginRight: "8px"}}/>,
                        }}
                        variant="outlined"
                        size="small"
                        style={{
                            backgroundColor: "white",
                            ...getRowStyle(params)
                        }}
                    />
                </div>);
            },
        },
    ];

    const handleClose = () => {
        // setBillTenantsModal(false);
    };

    const billTenants = async () => {
        console.log(billedTenants);
        console.log(elecBilledTenants);

        setBillLoading(true);

        let finalBill = [];

        if (billedTenants.length > elecBilledTenants.length) {
            billedTenants.forEach((tenant) => {
                elecBilledTenants.forEach((tenant2) => {
                    if (tenant.tenant_id === tenant2.tenant_id) {
                        finalBill = [...finalBill, {
                            tenant_id: tenant.tenant_id,
                            water_bill: tenant.finalWaterBill,
                            electricity_bill: tenant2.electricity_bill,
                            total_bill: parseInt(tenant.rent.replace(",", "")) + parseInt(tenant.arrears) + parseFloat(tenant2.electricity_bill) + parseFloat(tenant.finalWaterBill),
                            meter_reading: tenant.meter_reading,
                            electricity_meter_reading: tenant2.electricity_meter_reading,
                            landlord_id: JSON.parse(originalText)[0][1],
                        },];
                        billedTenants = billedTenants.filter((t) => t.tenant_id !== tenant2.tenant_id);
                        elecBilledTenants = elecBilledTenants.filter((t) => t.tenant_id !== tenant2.tenant_id);
                    }
                });
            });
        } else {
            elecBilledTenants.forEach((tenant) => {
                billedTenants.forEach((tenant2) => {
                    if (tenant.tenant_id === tenant2.tenant_id) {
                        finalBill = [...finalBill, {
                            tenant_id: tenant.tenant_id,
                            water_bill: tenant2.finalWaterBill,
                            electricity_bill: tenant.electricity_bill,
                            total_bill: parseInt(tenant.rent) + parseInt(tenant.arrears) + parseFloat(tenant.electricity_bill) + parseFloat(tenant2.finalWaterBill),
                            meter_reading: tenant2.meter_reading,
                            electricity_meter_reading: tenant.electricity_meter_reading,
                            landlord_id: JSON.parse(originalText)[0][1],
                        },];
                        billedTenants = billedTenants.filter((t) => t.tenant_id !== tenant2.tenant_id);
                        elecBilledTenants = elecBilledTenants.filter((t) => t.tenant_id !== tenant2.tenant_id);
                    }
                });
            });
        }

        let finalWaterBills = [];
        billedTenants.forEach((reading) => {
            reading = {
                tenant_id: reading.tenant_id,
                water_bill: reading.finalWaterBill,
                electricity_bill: 0,
                total_bill: parseInt(reading.rent) + parseInt(reading.arrears) + parseFloat(reading.finalWaterBill),
                meter_reading: reading.meter_reading,
                electricity_meter_reading: 0,
                landlord_id: JSON.parse(originalText)[0][1],
            };
            finalWaterBills = [...finalWaterBills, reading];
        });

        let finalElectricityBills = [];
        elecBilledTenants.forEach((reading) => {
            reading = {
                tenant_id: reading.tenant_id,
                water_bill: 0,
                electricity_bill: reading.electricity_bill,
                total_bill: parseInt(reading.rent) + parseInt(reading.arrears) + parseFloat(reading.electricity_bill),
                meter_reading: 0,
                electricity_meter_reading: reading.electricity_meter_reading,
                landlord_id: JSON.parse(originalText)[0][1],
            };
            finalElectricityBills = [...finalElectricityBills, reading];
        });

        finalBill = [...finalBill, ...finalWaterBills, ...finalElectricityBills];

        if (selectedMonth === null) {
            showSnackbar({
                title: "Error!",
                message: "Kindly select the month you are billing for",
                type: "error", // Options: success, error, warning, info
                position: {vertical: "bottom", horizontal: "right"}, // Options for position
            });

            setBillLoading(false);
            return;
        }

        let continueBilling = false;

        await confirm({
            description: `You are about to bill for the month of 
     ${getMonthName(parseInt(month.getMonth())).toUpperCase()} , ${parseInt(month.getFullYear())}`
        })
            .then(() => {
                continueBilling = true
            })
            .catch(() => continueBilling = false);

        if (!continueBilling) {
            fetch(ApiDetails + "pegasus/visionary/property/enableBilling", {
                method: "POST", body: JSON.stringify({
                    property_id: propertyDetails.property_id,
                }),
            })
                .then(async (response) => {
                    if (response.status === 200) {
                        window.location.reload(false);
                    } else {
                        showSnackbar({
                            title: "Error!",
                            message: "Failed updating tenant's details",
                            type: "error", // Options: success, error, warning, info
                            position: {vertical: "bottom", horizontal: "right"}, // Options for position
                        });

                    }
                })
                .catch((err) => {
                    console.log(err);
                    showSnackbar({
                        title: "Error!",
                        message: "Failed updating tenant's details",
                        type: "error", // Options: success, error, warning, info
                        position: {vertical: "bottom", horizontal: "right"}, // Options for position
                    });

                });
            return;
        }


        //Make Billing API Call
        fetch(ApiDetails + "pegasus/visionary/property/bill/tenants", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"  // Replace with your actual token variable
            },
            body: JSON.stringify([...finalBill, {
                month: parseInt(month.getMonth()),
                year: parseInt(month.getFullYear()),
                property_id: propertyDetails.property_id,
                property_name: propertyDetails.property_name,
                email_address: JSON.parse(originalText)[5][1],
            },]),
        })
            .then(async (response) => {
                let data = await response.json();
                if (response.status === 200) {
                    setHtmlContent(data.htmlReport);

                    showSnackbar({
                        title: "Success!",
                        message: "Tenants billed successfully",
                        type: "success", // Options: success, error, warning, info
                        position: {vertical: "bottom", horizontal: "right"}, // Options for position
                    });
                    setBillLoading(false);
                    // window.location.reload(false);

                } else {
                    showSnackbar({
                        title: "Error!",
                        message: "Failed billing tenants",
                        type: "error", // Options: success, error, warning, info
                        position: {vertical: "bottom", horizontal: "right"}, // Options for position
                    });
                    setBillLoading(false);

                    handleClose();
                }
            })
            .catch((err) => {
                console.log(err);
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong while billing tenants",
                    type: "error", // Options: success, error, warning, info
                    position: {vertical: "bottom", horizontal: "right"}, // Options for position
                });
                setBillLoading(false);

                handleClose();
            });
    };

    const downloadPDF = () => {
        //Make Billing API Call
        fetch(ApiDetails + "pegasus/visionary/property/bill/report", {
            method: "POST", body: JSON.stringify({
                pdf: htmlContent
            }),
        })
            .then(async (response) => {
                if (response.status === 200) {

                    let fileName;

                    for (let pair of response.headers.entries()) {
                        // accessing the entries
                        console.log(pair);
                        if (pair[0] === "content-disposition") {
                            // if (pair[0] === "FILE_NAME") {
                            // key I'm looking for in this instance
                            fileName = pair[1]; // saving that value where I can use it
                        }
                    }

                    showSnackbar({
                        title: "Success!",
                        message: "File download will start shortly",
                        type: "success", // Options: success, error, warning, info
                        position: {vertical: "bottom", horizontal: "right"}, // Options for position
                    });


                    let blob = await response.blob();

                    let href = window.URL.createObjectURL(blob);
                    // create "a" HTML element with href to file & click
                    const link = document.createElement("a");
                    link.href = href;
                    link.setAttribute("download", fileName); //or any other extension
                    document.body.appendChild(link);
                    link.click();

                    // clean up "a" element & remove ObjectURL
                    document.body.removeChild(link);
                    URL.revokeObjectURL(href);

                    handleClose();


                } else {
                    showSnackbar({
                        title: "Error!",
                        message: "Failed downloading report",
                        type: "error", // Options: success, error, warning, info
                        position: {vertical: "bottom", horizontal: "right"}, // Options for position
                    });
                    setBillLoading(false);

                    handleClose();
                }
            })
            .catch((err) => {
                console.log(err);
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong while downloading report",
                    type: "error", // Options: success, error, warning, info
                    position: {vertical: "bottom", horizontal: "right"}, // Options for position
                });
                setBillLoading(false);

                handleClose();
            });
    };

    // Calculate height for the DataGrid based on the number of rows
    // This ensures all tenants are visible without scrolling in the modal
    const calculateGridHeight = (rowCount) => {
        // Base height for header and footer
        const baseHeight = 56 + 56; // header + footer
        // Row height (adjust if your rows have custom height)
        const rowHeight = 52;
        // Calculate height needed for all rows plus some padding
        const contentHeight = rowCount * rowHeight + 20;
        // Return total height needed
        return baseHeight + contentHeight;
    };

    return (
        <>
            <Dialog
                fullWidth
                maxWidth="lg"
                open={billTenantsModal}
                TransitionComponent={Transition}
                keepMounted
                aria-describedby="alert-dialog-slide-description"
            >

                <DialogTitle sx={{
                    pb: 1,
                    background: `linear-gradient(45deg, ${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.light, 0.05)})`,
                    position: 'relative'
                }}>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                            <Box
                                sx={{
                                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                                    p: 1,
                                    borderRadius: 2,
                                    mr: 2,
                                    color: theme.palette.success.main
                                }}
                            >
                                <ReceiptLongIcon/>
                            </Box>
                            <Box>
                                <Typography variant="h6" fontWeight="bold">
                                    Bill Property Tenants
                                </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {propertyDetails.property_name} • {propertyDetails.location}
                                    </Typography>

                            </Box>
                        </Box>
                    </Box>
                </DialogTitle>


                <DialogContent sx={styles.dialogContent}>
                    {htmlContent !== "" ? (
                        <Box sx={{
                            borderRadius: '12px',
                            border: '1px solid #e0e0e0',
                            padding: '24px',
                            backgroundColor: '#fff',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                            width: '100%'
                        }}>
                            {/* Render the HTML content */}
                            <div dangerouslySetInnerHTML={{__html: htmlContent}}/>
                        </Box>
                    ) : (
                        <Box sx={styles.container}>
                            {/* Billing Month Selection */}
                            <Box sx={styles.monthSelection}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1}}>
                                            <CalendarMonthIcon color="primary"/>
                                            <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                                                Billing for which month?
                                            </Typography>
                                        </Box>

                                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                                            <DatePicker
                                                id="outlined-basic"
                                                views={["year", "month"]}
                                                value={month}
                                                onChange={(newValue) => {
                                                    console.log("Selected value :: ")
                                                    console.log(newValue)

                                                    setMonth(newValue);
                                                    setSelectedMonth(newValue);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        fullWidth
                                                        variant="outlined"
                                                        sx={{
                                                            backgroundColor: "white",
                                                            borderRadius: "8px",
                                                            "& .MuiOutlinedInput-root": {
                                                                fontSize: "14px",
                                                                padding: "10px",
                                                            },
                                                            "& .MuiInputLabel-root": {
                                                                fontSize: "14px",
                                                            },
                                                        }}
                                                        label="Select billing month and year"
                                                    />
                                                )}
                                            />
                                        </LocalizationProvider>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Box sx={styles.infoBox}>
                                            <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1}}>
                                                <InfoIcon color="info"/>
                                                <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                                                    Billing Information
                                                </Typography>
                                            </Box>

                                            <Box sx={{display: "flex", alignItems: "center", gap: 1, mt: 1}}>
                                                <WaterDropIcon fontSize="small" color="info"/>
                                                <Typography variant="body2">
                                                    Water rate: <strong>{waterBill} KSH</strong> per unit
                                                    (min: {minimumWaterBill} KSH)
                                                </Typography>
                                            </Box>

                                            <Box sx={{display: "flex", alignItems: "center", gap: 1, mt: 1}}>
                                                <ElectricBoltIcon fontSize="small" color="warning"/>
                                                <Typography variant="body2">
                                                    Electricity rate: <strong>{electricityBill} KSH</strong> per unit
                                                    (min: {minimumElectricityBill} KSH)
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* DataGrid container with fixed height to show all tenants */}
                            <Box sx={styles.gridWrapper}>
                                <StyledDataGrid
                                    initialState={{
                                        sorting: {sortModel: [{field: "house_number", sort: "asc"}]},
                                        pagination: {
                                            paginationModel: {
                                                pageSize: 10  // Set to show 10 tenants per page as requested
                                            }
                                        }
                                    }}
                                    rows={tenants}
                                    columns={billingColumns}
                                    disableSelectionOnClick
                                    rowsPerPageOptions={[10]}
                                    autoHeight={true} // Important: Auto height ensures all rows are visible
                                    sx={{
                                        border: "1px solid #e0e0e0",
                                        borderRadius: "8px",
                                        overflow: "visible",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                                        '& .MuiDataGrid-main': {overflow: 'visible'},
                                        height: calculateGridHeight(Math.min(tenants.length, 10)) + 'px', // Dynamic height based on rows
                                        minHeight: calculateGridHeight(Math.min(tenants.length, 10)) + 'px', // Ensure minimum height
                                        // Ensure content fits well:
                                        '& .MuiDataGrid-footerContainer': {
                                            position: 'static',
                                            bottom: 0
                                        }
                                    }}
                                />
                            </Box>
                        </Box>
                    )}
                </DialogContent>

                {htmlContent !== "" ? (
                    <DialogActions sx={{padding: "16px", borderTop: "1px solid #e0e0e0", backgroundColor: "#f9fafb"}}>
                        <Button
                            variant="contained"
                            startIcon={<DownloadIcon/>}
                            onClick={downloadPDF}
                            sx={styles.downloadButton}
                        >
                            Download PDF
                        </Button>

                        <Button
                            onClick={() => window.location.reload()}
                            variant="outlined"
                            sx={styles.cancelButton}
                        >
                            Cancel
                        </Button>
                    </DialogActions>
                ) : (
                    <DialogActions sx={{padding: "16px", borderTop: "1px solid #e0e0e0", backgroundColor: "#f9fafb"}}>
                        {/* Cancel Button */}
                        <Button onClick={() => window.location.reload()} variant="outlined"
                                sx={styles.cancelButton}>
                            Cancel
                        </Button>

                        {/* Bill Button */}
                        <LoadingButton
                            size="medium"
                            onClick={billTenants}
                            loading={billLoading}
                            startIcon={<SendIcon/>}
                            variant="contained"
                            sx={styles.billButton}
                            loadingPosition="start"
                        >
                            Bill
                        </LoadingButton>
                    </DialogActions>
                )}
            </Dialog>

            {/* Search header - replaces the old static header */}
            <Box sx={styles.searchHeader}>
                <Box sx={styles.propertyInfo}>
                    <Typography variant="h6" sx={{fontWeight: 600, color: "#2C5283"}}>
                        Property Tenants • {propertyDetails.property_name || "Property"}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {filteredTenants.length} of {tenantsLength} tenants {searchTerm && "matched search criteria"}
                    </Typography>
                </Box>

                {/* Search Box */}
                <SearchBox
                    placeholder="Search by name, house number or phone..."
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={searchTerm}
                    onChange={handleSearchChange}
                    sx={{maxWidth: "400px"}}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action"/>
                            </InputAdornment>
                        ),
                        endAdornment: searchTerm && (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="clear search"
                                    onClick={handleClearSearch}
                                    edge="end"
                                    size="small"
                                >
                                    <ClearIcon fontSize="small"/>
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            {!fetched ? (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 2,
                    border: '1px solid #e0e0e0'
                }}>
                    <CircularProgress
                        size={60}
                        thickness={4}
                        sx={{
                            color: theme.palette.primary.main,
                            mb: 2
                        }}
                    />
                    <Typography variant="h6" color="text.secondary" sx={{mb: 1}}>
                        Loading Tenants
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                        Fetching tenants ...
                    </Typography>
                </Box>
            ) : filteredTenants.length === 0 ? (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 2,
                    border: '1px solid #e0e0e0'
                }}>
                    <PeopleAltIcon
                        sx={{
                            fontSize: 60,
                            color: theme.palette.text.disabled,
                            mb: 2
                        }}
                    />
                    <Typography variant="h6" color="text.secondary" sx={{mb: 1}}>
                        No Tenants Found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        There are no tenants in this property yet.
                    </Typography>
                </Box>
            ) : (
                <Box sx={{
                    width: '100%',
                    marginBottom: 4,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    borderRadius: "8px",
                    overflow: "hidden"
                }}>
                    <StyledDataGrid
                        rows={filteredTenants}
                        columns={columns}
                        disableSelectionOnClick
                        initialState={{
                            sorting: {sortModel: [{field: "house_number", sort: "asc"}]},
                            pagination: {
                                paginationModel: {
                                    pageSize: 10  // Set to show 10 tenants per page as requested
                                }
                            }
                        }}
                        rowsPerPageOptions={[10]}
                        autoHeight
                        sx={{
                            border: "1px solid #e0e0e0"
                        }}
                    />
                </Box>
            )}
        </>
    );
};

export default PropertyTenants;